import { supabase } from '@/lib/supabase';

/**
 * Technician activity lookups.
 *
 * These used to live in lib/utils.ts. They were the only three things in that
 * module that touched the database, and their `supabase` import meant every
 * consumer of a pure helper - `cn()`, `formatCurrency()`, the business-hours
 * maths - transitively constructed a Supabase client at module load. Since
 * lib/supabase.ts throws when its env vars are absent, that made the pure
 * helpers untestable anywhere without a populated .env.
 *
 * Data access belongs in lib/api/ with the rest of it.
 */

/**
 * Fetch the set of dates (YYYY-MM-DD) a technician was active on the site.
 * A tech is "active" on a day if they have at least one audit_log entry
 * with a timestamp between 08:00-17:00 on a weekday.
 */
export async function getTechActiveDays(userId: string, from: Date, to: Date): Promise<Set<string>> {
  const { data } = await supabase
    .from('audit_log')
    .select('created_at')
    .eq('user_id', userId)
    .gte('created_at', from.toISOString())
    .lte('created_at', to.toISOString());

  const days = new Set<string>();
  (data || []).forEach((r: any) => {
    const d = new Date(r.created_at);
    const dow = d.getDay();
    const hour = d.getHours();
    if (dow >= 1 && dow <= 5 && hour >= 8 && hour < 17) {
      days.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    }
  });
  return days;
}

/**
 * Check if a technician is unmanaged (managed_by IS NULL).
 * Returns { isUnmanaged, userId } for the given technician ID.
 */
export async function getTechManagedStatus(technicianId: string): Promise<{ isUnmanaged: boolean; userId: string | null }> {
  const { data } = await supabase
    .from('technicians')
    .select('user_id, managed_by')
    .eq('id', technicianId)
    .single();
  return { isUnmanaged: !data?.managed_by, userId: data?.user_id || null };
}

/**
 * For an unmanaged technician, fetch their active days in the given range.
 * For managed technicians, returns undefined (use all business days).
 */
export async function getActiveDaysIfUnmanaged(technicianId: string, from: Date, to: Date): Promise<Set<string> | undefined> {
  const { isUnmanaged, userId } = await getTechManagedStatus(technicianId);
  if (!isUnmanaged || !userId) return undefined;
  return getTechActiveDays(userId, from, to);
}
